import { useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { PageHero } from "../components/shared/Primitives";

/* Public, static comparison page — customer-facing wording only.
   No internal entitlement names, no owner-only badges. One continuous
   page, no tabs. Every row repeats its own tick/cross per membership
   (no "everything in the previous tier, plus"). Reuses existing
   verified entitlement data and existing routes only — no backend or
   entitlement changes. */

const CARER_COLUMNS = [
  { key: "carer-free", name: "Carer Free", price: "Free", priceNote: null, trial: "No trial needed — free membership", ctaLabel: "Join free", ctaHref: "/join/carer-starter" },
  { key: "carer-essentials", name: "Carer Essentials", price: "£7", priceNote: "/ month", trial: "No free trial", ctaLabel: "Start Carer Essentials", ctaHref: "/membership/carer-essentials" },
  { key: "pro-carer-office", name: "Pro Carer Office", price: "£19", priceNote: "/ month", trial: "7-day free trial", ctaLabel: "Start your free 7-day trial", ctaHref: "/membership/pro-carer-circle" },
];

const FAMILY_COLUMNS = [
  { key: "family-starter", name: "Family Starter", price: "Free", priceNote: null, trial: "No trial needed — free membership", ctaLabel: "Join free", ctaHref: "/join/family-starter" },
  { key: "family-care-office", name: "Family Care Office", price: "£19", priceNote: "/ month", trial: "No free trial", ctaLabel: "Set up Family Care Office", ctaHref: "/membership/family-circle" },
];

const CARER_GROUPS = [
  {
    group: "Finding work and connecting with families",
    rows: [
      { label: "Create and edit your carer profile", values: [true, true, true] },
      { label: "Share your approved profile with families", note: "Once our team has approved your profile.", values: [true, true, true] },
      { label: "Browse families' Care Needs", note: "Subject to carer-review approval on your account.", values: [true, true, true] },
      { label: "Express interest in suitable care opportunities", note: "Subject to carer-review approval on your account.", values: [true, true, true] },
      { label: "Receive invitations from families", values: [true, true, true] },
      { label: "Use a private Care Discussion Room with each family", values: [true, true, true] },
      { label: "Discuss duties, hours, nights, breaks and expectations before accepting", values: [true, true, true] },
    ],
  },
  {
    group: "Organising professional records",
    rows: [
      { label: "Store details of important professional documents", values: [false, true, true] },
      { label: "Track document expiry and renewal dates", values: [false, true, true] },
      { label: "Record training courses", values: [false, true, true] },
      { label: "Track CPD hours", values: [false, true, true] },
      { label: "Keep a private references record", values: [false, true, true] },
      { label: "Keep a record of previous care work and placements", values: [false, true, true] },
    ],
  },
  {
    group: "Free templates",
    note: "Available in all three carer memberships.",
    rows: [
      { label: "Basic Service Agreement", values: [true, true, true] },
      { label: "Simple Invoice Template", values: [true, true, true] },
      { label: "Daily Care Notes", values: [true, true, true] },
      { label: "Basic Emergency Information Form", values: [true, true, true] },
      { label: "Self-Employed Carer Checklist", values: [true, true, true] },
    ],
  },
  {
    group: "Pro Carer Office agreements and payment tools",
    rows: [
      { label: "Deposit & Payment Agreement", values: [false, false, true] },
      { label: "Care Schedule & Expectations Template", values: [false, false, true] },
      { label: "Change of Care Terms Form", values: [false, false, true] },
      { label: "Hourly Rate Calculator", values: [false, false, true] },
      { label: "Live-In Care Rate Calculator", values: [false, false, true] },
      { label: "Tax Confidence Kit", values: [false, false, true] },
    ],
  },
  {
    group: "Shift Kit — your everyday on-shift forms",
    rows: [
      { label: "Arrival Check (Home + Client Condition)", values: [false, false, true] },
      { label: "Daily Medication Record", values: [false, false, true] },
      { label: "Handover Notes", values: [false, false, true] },
      { label: "Food & Fluid Chart", values: [false, false, true] },
      { label: "Mood / Behaviour Notes", values: [false, false, true] },
      { label: "Daily Care Log & Notes Sheet", values: [false, false, true] },
    ],
  },
  {
    group: "Professional guidance and development",
    rows: [
      { label: "Pricing & Income", note: "Setting and reviewing your care rates; understanding income and working costs.", values: [false, false, true] },
      { label: "Tax & Admin", note: "Tax and self-employed administration.", values: [false, false, true] },
      { label: "Payment Protection", note: "Invoicing, payment protection, deposits and responsible payers.", values: [false, false, true] },
      { label: "Contracts & Agreements", note: "Contracts and working agreements for every placement.", values: [false, false, true] },
      { label: "Working Independently", note: "Working independently and professional boundaries.", values: [false, false, true] },
      { label: "Safety & Escalation", note: "Recognising risk, responding safely, and what to document.", values: [false, false, true] },
      { label: "Training & CPD guidance", note: "Course bundles, providers and a clear path.", values: [false, false, true] },
      { label: "Pro Carer Advantage Series", values: [false, false, true] },
    ],
  },
];

const FAMILY_GROUPS = [
  {
    group: "Finding and speaking with carers",
    rows: [
      { label: "Browse approved carer profiles", values: [true, true] },
      { label: "Post your Care Needs for free", values: [true, true] },
      { label: "Receive interest from carers", values: [true, true] },
      { label: "Invite a carer to talk privately", values: [true, true] },
      { label: "Use a separate Care Discussion Room for each carer", values: [true, true] },
      { label: "Discuss care, hours, nights, duties, breaks and expectations", values: [true, true] },
      { label: "Keep messages and shared documents together", values: [true, true] },
      { label: "View all carer connections for the person receiving care", values: [true, true] },
    ],
  },
  {
    group: "Organising care after choosing a carer",
    rows: [
      { label: "Create a separate Care Arrangement for each carer", values: [false, true] },
      { label: "Record each carer's rate, rota, duties, breaks and notice terms", values: [false, true] },
      { label: "Let each carer review and acknowledge their own Arrangement", values: [false, true] },
      { label: "Set a date to review an Arrangement", values: [false, true] },
      { label: "Record important care and health information once", values: [false, true] },
      { label: "Record household, access and practical information once", values: [false, true] },
      { label: "Record emergency contacts and important instructions", values: [false, true] },
      { label: "Prepare starting-care and handover information", values: [false, true] },
      { label: "Choose which agreed carers can see that shared information", values: [false, true] },
      { label: "Remove a carer's access without affecting the other carers", values: [false, true] },
    ],
  },
];

const CARER_INCLUDED_RECAP = [
  { heading: "Free templates — included in all three carer memberships", items: ["Basic Service Agreement", "Simple Invoice Template", "Daily Care Notes", "Basic Emergency Information Form", "Self-Employed Carer Checklist"] },
  { heading: "Pro Carer Office agreements & payment tools", items: ["Deposit & Payment Agreement", "Care Schedule & Expectations Template", "Change of Care Terms Form", "Hourly Rate Calculator", "Live-In Care Rate Calculator", "Tax Confidence Kit"] },
  { heading: "Shift Kit forms (Pro Carer Office)", items: ["Arrival Check (Home + Client Condition)", "Daily Medication Record", "Handover Notes", "Food & Fluid Chart", "Mood / Behaviour Notes", "Daily Care Log & Notes Sheet"] },
  { heading: "Guidance & resource sections (Pro Carer Office)", items: ["Pricing & Income", "Tax & Admin", "Payment Protection", "Contracts & Agreements", "Working Independently", "Safety & Escalation", "Training & CPD guidance", "Pro Carer Advantage Series"] },
];

const FAMILY_INCLUDED_RECAP = [
  { heading: "Records created for you (Family Care Office)", items: ["An individual Care Arrangement for every carer you work with", "One shared \u201cGetting Ready for Care\u201d record for the person receiving care"] },
];

function StatusIcon({ included }) {
  return included ? (
    <span className="inline-flex items-center gap-1.5 text-teal font-medium" title="Included">
      <Check className="w-4 h-4" strokeWidth={2.75} aria-hidden="true" />
      <span className="sr-only">Included</span>
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-ink-soft" title="Not included">
      <X className="w-4 h-4" strokeWidth={2.5} aria-hidden="true" />
      <span className="sr-only">Not included</span>
    </span>
  );
}

function MatrixSection({ columns, groups, accent }) {
  const accentText = accent === "purple" ? "text-purple" : "text-teal";
  const accentBg = accent === "purple" ? "bg-purple-50" : "bg-teal-50";
  return (
    <div className="mt-8">
      {/* Desktop / tablet table */}
      <div className="hidden md:block rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm" data-testid={`matrix-table-${accent}`}>
          <thead>
            <tr className={accentBg}>
              <th className="text-left font-semibold px-5 py-4 w-2/5">What you get</th>
              {columns.map((c) => (
                <th key={c.key} className="text-left font-semibold px-5 py-4" data-testid={`matrix-col-${c.key}`}>
                  <div className={accentText}>{c.name}</div>
                  <div className="font-normal text-ink mt-0.5">{c.price}{c.priceNote && <span className="text-ink-soft">{c.priceNote}</span>}</div>
                  <div className="font-normal text-xs text-ink-soft mt-0.5">{c.trial}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.group}>
                <tr className="bg-slate-50">
                  <td colSpan={columns.length + 1} className="px-5 py-2.5 font-semibold text-ink-soft text-xs uppercase tracking-wide">
                    {g.group}{g.note && <span className="ml-2 font-normal normal-case text-ink-soft/80">({g.note})</span>}
                  </td>
                </tr>
                {g.rows.map((r) => (
                  <tr key={r.label} className="border-t border-slate-100" data-testid={`matrix-row-${r.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    <td className="px-5 py-3 align-top">
                      <div>{r.label}</div>
                      {r.note && <div className="text-xs text-ink-soft mt-0.5">{r.note}</div>}
                    </td>
                    {r.values.map((v, i) => (
                      <td key={i} className="px-5 py-3 align-top"><StatusIcon included={v} /></td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile stacked view — retains every benefit + status, per row */}
      <div className="md:hidden space-y-6" data-testid={`matrix-mobile-${accent}`}>
        {groups.map((g) => (
          <div key={g.group}>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-soft mb-2">
              {g.group}{g.note && <span className="ml-1 font-normal normal-case">({g.note})</span>}
            </p>
            <div className="space-y-3">
              {g.rows.map((r) => (
                <div key={r.label} className="rounded-xl border border-slate-200 p-4" data-testid={`matrix-mobile-row-${r.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <p className="font-medium text-sm">{r.label}</p>
                  {r.note && <p className="text-xs text-ink-soft mt-0.5">{r.note}</p>}
                  <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
                    {columns.map((c, i) => (
                      <span key={c.key} className="inline-flex items-center gap-1.5 text-xs">
                        <StatusIcon included={r.values[i]} />
                        <span className="text-ink-soft">{c.name}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function IncludedRecap({ recap, accent }) {
  const accentText = accent === "purple" ? "text-purple" : "text-teal";
  return (
    <div className="mt-10 grid sm:grid-cols-2 gap-6" data-testid={`included-recap-${accent}`}>
      {recap.map((block) => (
        <div key={block.heading} className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className={`text-sm font-semibold ${accentText}`}>{block.heading}</p>
          <ul className="mt-2.5 space-y-1.5">
            {block.items.map((item) => (
              <li key={item} className="text-sm text-ink-muted flex items-start gap-2">
                <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${accentText}`} aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function CtaRow({ columns, accent }) {
  const btnClass = accent === "purple" ? "btn-purple" : "btn-teal";
  return (
    <div className="mt-10 flex flex-wrap gap-4" data-testid={`cta-row-${accent}`}>
      {columns.map((c) => (
        <a key={c.key} href={c.ctaHref} className={`${btnClass} text-sm`} data-testid={`compare-cta-${c.key}`}>
          {c.ctaLabel} — {c.price}{c.priceNote || ""}
        </a>
      ))}
    </div>
  );
}

export default function CompareMemberships() {
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, []);

  return (
    <div data-testid="page-compare-memberships">
      <PageHero
        eyebrow="Memberships"
        title="Compare JCC Memberships"
        description="Start free and choose additional organisation, guidance and support when you need it. JCC does not take commission from care arrangements."
      />

      <section id="comparison-choices" className="pt-10 scroll-mt-24" data-testid="compare-jump-section">
        <div className="container-jcc">
          <p className="text-sm text-ink-muted text-center mb-4">Jump straight to the comparison that matters to you:</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#carer-memberships" className="btn-purple text-sm justify-center" data-testid="jump-to-carer-memberships">
              Compare Carer Memberships
            </a>
            <a href="#family-memberships" className="btn-teal text-sm justify-center" data-testid="jump-to-family-memberships">
              Compare Family Memberships
            </a>
          </div>
        </div>
      </section>

      <section id="carer-memberships" className="section pt-8 scroll-mt-24">
        <div className="container-jcc">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-purple">Carer Memberships</h2>
          <p className="mt-2 text-ink-muted max-w-2xl">
            Every row below shows exactly what each carer membership includes — nothing is assumed carried over from another tier.
          </p>
          <p className="mt-4 text-sm">
            <a href="#family-memberships" className="text-teal hover:underline font-medium" data-testid="jump-to-family-inline">
              Jump to Family Memberships ↓
            </a>
          </p>
          <MatrixSection columns={CARER_COLUMNS} groups={CARER_GROUPS} accent="purple" />
          <p className="mt-8 font-semibold text-sm">What's included — full names</p>
          <IncludedRecap recap={CARER_INCLUDED_RECAP} accent="purple" />
          <CtaRow columns={CARER_COLUMNS} accent="purple" />
          <p className="mt-6 text-sm">
            <a href="#comparison-choices" className="text-ink-muted hover:text-ink hover:underline" data-testid="back-to-comparison-choices-carer">
              Back to comparison choices ↑
            </a>
          </p>
        </div>
      </section>

      <section id="family-memberships" className="section pt-4 scroll-mt-24">
        <div className="container-jcc">
          <div className="pt-10 border-t border-slate-200">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-teal">Family Memberships</h2>
            <p className="mt-2 text-ink-muted max-w-2xl">
              Every row below shows exactly what each family membership includes — nothing is assumed carried over from another tier.
            </p>
            <MatrixSection columns={FAMILY_COLUMNS} groups={FAMILY_GROUPS} accent="teal" />
            <p className="mt-8 font-semibold text-sm">What's included — full names</p>
            <IncludedRecap recap={FAMILY_INCLUDED_RECAP} accent="teal" />
            <CtaRow columns={FAMILY_COLUMNS} accent="teal" />
            <p className="mt-6 text-sm">
              <a href="#comparison-choices" className="text-ink-muted hover:text-ink hover:underline" data-testid="back-to-comparison-choices-family">
                Back to comparison choices ↑
              </a>
            </p>
          </div>
        </div>
      </section>

      <section className="section pt-4 pb-16">
        <div className="container-jcc">
          <p className="text-center text-xs text-ink-soft max-w-xl mx-auto">
            Prices shown are per month, billed by our payment processor. You can view or cancel your membership at any
            time from your account billing page. Cancelling keeps any free access included at a lower tier.
          </p>
          <p className="mt-4 text-center text-sm">
            <Link to="/contact" className="text-ink-muted hover:text-ink underline" data-testid="compare-contact-link">
              Not sure which is right for you? Get in touch.
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
